import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, AsyncPipe } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  IconBackComponent,
  IconClockComponent,
  IconLoadingComponent,
} from '@pedix-workspace/angular-ui-icons';
import {
  CategoryAvailabilityDialogComponent,
  ItemListComponent,
  ItemListGroupProductsByCategoryPipe,
  ProductListItemComponent,
} from '@pedix-workspace/pedixapp-shared-catalog';
import {
  EstablishmentUrlPipe,
  IsCategoryAvailablePipe,
} from '@pedix-workspace/pedixapp-shared-pipes';
import { EstablishmentsCheckoutService } from '../../../order-checkout/establishments-checkout.service';
import { AppRequestContextService } from '../../../../app-request-context.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ApiV1_ProductTag } from '@pedix-workspace/api-v1';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { AppSearchResultsService } from '../../../../app-search-results.service';
import { StockIsOutOfStockPipe } from '@pedix-workspace/pedixapp-stock';
import { ShareButtonComponent } from '../../../main/share-button/share-button.component';
import { CartPromotionsService } from '../../../../cart-promotions.service';

@Component({
  selector: 'app-products-by-tag',
  templateUrl: './products-by-tag.component.html',
  styleUrl: './products-by-tag.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    IconBackComponent,
    IconClockComponent,
    ButtonComponent,
    RouterLink,
    ProductListItemComponent,
    TranslocoDirective,
    IsCategoryAvailablePipe,
    AlertComponent,
    CategoryAvailabilityDialogComponent,
    ItemListComponent,
    ItemListGroupProductsByCategoryPipe,
    StockIsOutOfStockPipe,
    AsyncPipe,
    IconLoadingComponent,
    ShareButtonComponent,
  ],
})
export class ProductsByTagComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private appRequestContextService = inject(AppRequestContextService);
  private establishmentsCheckoutService = inject(EstablishmentsCheckoutService);
  readonly purchaseOptions = this.establishmentsCheckoutService.purchaseOptions;
  private establishmentUrlPipe = inject(EstablishmentUrlPipe);
  private t = inject(TranslocoService);

  private searchResultsService = inject(AppSearchResultsService);
  private destroyRef = inject(DestroyRef);
  private cartPromotionsService = inject(CartPromotionsService);

  readonly productTag = signal<ApiV1_ProductTag | null>(null);

  readonly establishment = computed(() => this.appRequestContextService.establishment());
  readonly categories = computed(() => this.appRequestContextService.allCategories());
  readonly tagId = computed(() => this.productTag()?.id);
  readonly productsByTag = computed(() =>
    this.searchResultsService.getFilteredResults(this.appRequestContextService.activeProducts(), {
      productTagIds: [this.tagId()],
    }),
  );

  readonly currencySymbol = computed(
    () => this.appRequestContextService.currencyLocaleData().currencySymbol,
  );
  readonly displaySku = computed(() => this.appRequestContextService.shouldDisplaySku());

  readonly displayShareButton = computed(() => {
    return this.establishment().seoSocialMedia
      ? this.establishment().seoSocialMedia.displayShareButton
      : true;
  });
  readonly shareUrl = computed(() => {
    const subsidiarySelector = this.appRequestContextService.subsidiarySelector();
    const establishment = subsidiarySelector
      ? this.appRequestContextService.subsidiaryParentCatalog()?.establishment
      : this.establishment();

    return this.establishmentUrlPipe.transform(establishment, ['productos-relacionados'], {
      searchParams: { t: this.productTag()?.slug },
    });
  });

  ngOnInit(): void {
    this.activatedRoute.data
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ productTag }) => {
        this.productTag.set(productTag);
      });
  }

  getPromotionsForProduct(productId: string) {
    return this.cartPromotionsService.availablePromotionsByProductId().get(productId) ?? [];
  }
}
